import type {FormEvent, ReactNode} from "react";
import {Filter, RotateCcw, Search} from "lucide-react";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";

type FilterBarProps = {
  onSubmit?: (e: FormEvent) => void;
  onReset?: () => void;
  children?: ReactNode;
  className?: string;
  /** When set, search renders first in the same card as filters */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Optional result count shown on the right (e.g. page size) */
  itemCount?: number;
};

/**
 * Unified list controls: Search (optional) → Filters → Reset / Apply.
 * One card — do not pair with AdminModernTable search; use hideSearch there.
 */
export function AdminFilterBar({
  onSubmit,
  onReset,
  children,
  className,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  itemCount
}: FilterBarProps) {
  const hasSearch = typeof searchValue === "string" && typeof onSearchChange === "function";
  const hasFilters = Boolean(children);

  return (
    <form
      className={cn("admin-filter-bar w-full", hasSearch && "admin-filter-bar--with-search", className)}
      onSubmit={onSubmit || ((e) => e.preventDefault())}
    >
      <div className="admin-filter-bar__row">
        {hasSearch ? (
          <AdminFilterSearch
            value={searchValue!}
            onChange={onSearchChange!}
            placeholder={searchPlaceholder}
            className="admin-filter-bar__search"
          />
        ) : null}
        {hasFilters ? <div className="admin-filter-bar__filters">{children}</div> : null}
        <div className="admin-filter-bar__trailing">
          {typeof itemCount === "number" ? (
            <p className="admin-filter-bar__count admin-caption tabular-nums">
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </p>
          ) : null}
          <AdminFilterActions onReset={onReset} />
        </div>
      </div>
    </form>
  );
}

export function AdminFilterField({label, children, className}: {label?: string; children: ReactNode; className?: string}) {
  return (
    <div className={cn("admin-filter-field flex min-w-[140px] flex-col gap-1", className)}>
      {label ? <Label className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">{label}</Label> : null}
      {children}
    </div>
  );
}

export function AdminFilterSearch({
  value,
  onChange,
  placeholder = "Search...",
  className
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative min-w-0", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        className="h-10 border-border/70 bg-background pl-9"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function AdminFilterActions({onReset}: {onReset?: () => void}) {
  return (
    <div className="admin-filter-bar__actions flex shrink-0 items-center gap-2">
      {onReset ? (
        <Button type="button" variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      ) : null}
      <Button type="submit" size="sm">
        <Filter className="h-4 w-4" />
        Apply filters
      </Button>
    </div>
  );
}
